import { useEffect, useState } from "react";
import Button from "../../ui/buttons/Button";
import rocket from "../../assets/icons/rocket.svg";
import rocketHover from "../../assets/icons/rocket-hover.svg";
import { ReactComponent as AddIcon } from "../../assets/icons/add.svg";
import { templatesService } from "../../services/templates.service.js";
import { accountsService } from "../../services/accounts.service.js";
import FloatingModal from "../../ui/Modals/FloatingModal";
import DropdownSelect from "../../ui/dropdowns/DropdownSelect.jsx";
import { selectFile, validateAttachmentSize } from "../../utils/fileUtils";
import styles from "../../styles/components/templates/NewAttachmentModal.module.css";

const FILE_PLACEHOLDER = "Elegir archivo...";
const ACCEPTED_FILE_TYPES = ".pdf,.doc,.docx,.png,.jpg";

function NewAttachmentModal({ onClose, onAttachmentCreated, onNewAttachmentError }) {
  const [rocketHovered, setRocketHovered] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedFileToUpload, setSelectedFileToUpload] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    accountsService
      .list()
      .then((accountsList) =>
        setAccounts(Array.isArray(accountsList) ? accountsList : []),
      )
      .catch(() => {
        setAccounts([]);
        onNewAttachmentError?.(
          "No se pudieron cargar las cuentas disponibles.",
        );
      });
  }, [onNewAttachmentError]);

  const handlePickFile = async () => {
    const file = await selectFile({ accept: ACCEPTED_FILE_TYPES });

    if (!file) return;

    const sizeErrorMessage = validateAttachmentSize({ fileSize: file.size });

    if (sizeErrorMessage) {
      onNewAttachmentError?.(sizeErrorMessage);
      return;
    }

    setSelectedFileToUpload(file);
  };

  const disableSend = !selectedFileToUpload || !selectedAccount || isUploading;

  const sendAttachment = async () => {
    if (disableSend) return;

    const formData = new FormData();
    formData.append("file", selectedFileToUpload);
    formData.append("account_id", String(selectedAccount.id));

    setIsUploading(true);

    try {
      await templatesService.createTemplateFileAttachment(formData);

      if (onAttachmentCreated) {
        await onAttachmentCreated();
      }
      onClose();
    } catch (err) {
      const errorData = err?.response?.data;
      const nameErrorMessage = Array.isArray(errorData?.name)
        ? errorData.name.join(" ")
        : errorData?.name;

      onNewAttachmentError?.(
        nameErrorMessage ||
          errorData?.detail ||
          "No se pudo subir el archivo adjunto.",
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <div className={styles.backdrop} onClick={onClose} />
      <FloatingModal bottom={128}>
        <Button
          type="button"
          iconLeft={<AddIcon />}
          onClick={handlePickFile}
          disabled={isUploading}
          height={50}
          title={selectedFileToUpload?.name}
          className={styles.filePickerButton}
          labelStyle={{
            display: "block",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {selectedFileToUpload ? selectedFileToUpload.name : FILE_PLACEHOLDER}
        </Button>

        <div className={styles.accountSelectWrapper}>
          <DropdownSelect
            placeholder="Seleccionar cuenta..."
            items={accounts}
            selectedItem={selectedAccount}
            onChange={setSelectedAccount}
            disabled={isUploading}
            labelKey="name"
            isEditing
          />
        </div>

        <Button
          tone="blue"
          iconRight={
            <img
              src={rocketHovered ? rocketHover : rocket}
              alt=""
              className={styles.rocketIcon}
            />
          }
          className={styles.sendButton}
          height={50}
          disabled={disableSend}
          loading={isUploading}
          onClick={sendAttachment}
          onMouseEnter={() => setRocketHovered(true)}
          onMouseLeave={() => setRocketHovered(false)}
        >
          Subir Archivo
        </Button>
      </FloatingModal>
    </>
  );
}

export default NewAttachmentModal;
